import { Box, Flex, HStack, Spacer, Spinner, Text } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { StatCard } from '../StatsCard'
import { FiCheckCircle, FiDollarSign, FiMessageSquare, FiTrendingDown, FiTrendingUp } from 'react-icons/fi'
import SixMonthTrend from '../Charts/SixMonthTrendChart'
import { AiOutlineLineChart } from 'react-icons/ai'
import MessageDistributionChart from '../Charts/MessageDistributionChart'
import { BsActivity, BsBarChart, BsStopwatch } from 'react-icons/bs'
import { ChannelCard } from './ChannelsUsage'
import ChartSkeleton from '../Charts/Skeleton'
import { formatNumber } from '../../../utils/DefaultFunction'
import API from '../../../helpers/axiosInstance'
import moment from 'moment'
import { DeliveryDistributionCards } from './DeliveryDistributionCard'
import { getChannelLabel } from '../../../utils/channels'

const UsageAnalytics = ({ date, refresh }) => {

    const [loading, setLoading] = useState(false);
    const [analyticsData, setAnalyticsData] = useState(null);

    const getApiHeaders = () => ({
        "x-api-key": localStorage.getItem("api-key"),
        "x-access-token": localStorage.getItem("customer-portal"),
    });
    const dateformated = moment(date);
    const month = dateformated.format("MM"); // "09"
    const year = dateformated.format("YYYY");
    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/dashboard/customer/usage-analytic?month=${month}&year=${year}${refresh}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            setAnalyticsData(response.data.data);
        } catch (error) {
            console.error("Error fetching analytics:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    if (loading || !analyticsData) {
        return (
            <Flex justify="center" align="center" h="300px">
                <Spinner size="lg" />
            </Flex>
        );
    }


    const channelConfig = {
        "SMS": {

            color: "#3b82f6",

        },
        "WhatsApp": {

            color: "#10b981",

        },
        "E-Mail": {

            color: "#f59e0b",

        },
        "Voice (IVR)": {

            color: "var(--brand-primary)",

        },
        "Push Notification": {

            color: "#ef4444",

        },
        "In-App Notification": {

            color: "#8b5cf6",

        },

    };

    const hourlyData = analyticsData?.hourlyDistribution;
    const allZero =
        hourlyData?.length > 0 && hourlyData.every((item) => item.count === 0);

    return (
        <>
            <Box>
                <Flex gap={6} wrap="wrap" justifyContent="space-between">
                    <StatCard
                        title="Total Messages"
                        value={formatNumber(analyticsData?.totalMessage, "en-US", true)}
                        change={`All channels combined`}
                        icon={<FiMessageSquare />}
                        iconColor="#64748b"
                        originalValue={formatNumber(analyticsData?.totalMessage)}
                    />
                    <StatCard
                        title="Avg Processing Time"
                        value={`${analyticsData?.avgProcessTime}s`}
                        change={`Message processing`}

                        icon={<FiCheckCircle />}
                        iconColor="#64748b"
                    />
                    <StatCard
                        title="Avg Delivery Time"
                        value={`${analyticsData?.avgDlvTime || 0}s`}
                        change="End-to-end delivery"

                        icon={<BsStopwatch />}
                        iconColor="#64748b"
                    />
                    <StatCard
                        title="Cost Per Message"
                        value={`${analyticsData?.customer_currency} ${analyticsData?.costPerMsg || 0}`}
                        change="Weighted average"

                        icon={<FiDollarSign />}
                        iconColor="#64748b"
                    />
                </Flex>
            </Box>


            <Box className="box-style-card" my={4}>
                <Flex direction="column" mb={4}>
                    <Flex align="center" gap={2}>

                        <Text fontSize="md" fontWeight="semibold" color="gray.700">Channel Usage Breakdown
                        </Text>
                    </Flex>
                    <Text fontSize="sm" color="#64748b">Detailed message volume and performance by channel</Text>
                </Flex>
                <DeliveryDistributionCards distributionData={analyticsData.deliveryBuckets} />
                <Spacer height={4} />
                {analyticsData?.channelUsage?.length > 0 ? <>
                    {analyticsData?.channelUsage?.map((ch, idx) => (
                        <ChannelCard customer_currency={analyticsData?.customer_currency} key={idx} {...ch} />
                    ))}
                </> : <ChartSkeleton title={"Channel usage analytics will appear here"} />}

            </Box>
            <Box className="box-style-card" my={4}>
                <Flex direction="column" mb={4}>
                    <Flex align="center" gap={2}>
                        <AiOutlineLineChart size={20} color="#155dfc" />
                        <Text fontSize="md" fontWeight="semibold" color="gray.700">6-Month Usage Trends
                        </Text>
                    </Flex>
                    <Text fontSize="sm" color="#64748b">Message volume trends across all channels</Text>
                </Flex>
                {Object.entries(analyticsData?.last6MonthsUsage)?.length > 0 ? <SixMonthTrend data={analyticsData?.last6MonthsUsage} /> : <ChartSkeleton title={"Usage trend analytics will appear here"} />}

            </Box>
            <Box className="box-style-card" my={4}>
                <Flex direction="column" mb={4}>
                    <Flex align="center" gap={2}>
                        <BsBarChart size={20} color="#155dfc" />
                        <Text fontSize="md" fontWeight="semibold" color="gray.700">Hourly Messages Distribution
                        </Text>
                    </Flex>
                    <Text fontSize="sm" color="#64748b">Message volume breakdown by hour of the day (last 24 hours)</Text>
                </Flex>
                <>
                    {allZero ? (
                        <ChartSkeleton title="Hourly message distribution will appear here" />

                    ) : (
                        <MessageDistributionChart
                            data={analyticsData?.hourlyDistribution}
                            avg={analyticsData?.avgPerHour}
                        />
                    )}
                </>
                {/* {analyticsData?.hourlyDistribution?.length > 0 ? <MessageDistributionChart data={analyticsData?.hourlyDistribution} avg={analyticsData?.avgPerHour} /> : <ChartSkeleton title={"Hourly message distribution will appear here"} />} */}


                <Flex gap={4} flexWrap="wrap" mt={4}>
                    {/* Peak Hour Card */}
                    <Box
                        bg="var(--brand-tint)"

                        borderRadius="15px"
                        p={5}
                        flex="1"
                        minW="200px"
                    >
                        <HStack spacing={2} mb={2}>
                            <FiTrendingUp size={18} color="#155dfc" />
                            <Text fontSize="sm" fontWeight="medium" color="#193cb8">
                                Peak Hour
                            </Text>
                        </HStack>

                        <Text fontSize="2xl" fontWeight="bold" color="var(--brand-primary-hover)">
                            {analyticsData?.peakHour?.hour || 0}
                        </Text>

                        <Text fontSize="xs" color="#155dfc" mt={1}>
                            {analyticsData?.peakHour?.count} messages
                        </Text>
                    </Box>

                    {/* Avg/Hour Card */}
                    <Box
                        bg="#f0fdf4"
                        borderRadius="15px"
                        p={5}
                        flex="1"
                        minW="200px"
                    >
                        <HStack spacing={2} mb={2}>
                            <BsActivity size={18} color="#00a63e" />
                            <Text fontSize="sm" fontWeight="medium" color="#016630">
                                Avg/Hour
                            </Text>
                        </HStack>

                        <Text fontSize="2xl" fontWeight="bold" color="#016630">
                            {analyticsData?.avgPerHour}

                        </Text>

                        <Text fontSize="xs" color="#00a63e" mt={1}>
                            messages per hour
                        </Text>
                    </Box>

                    {/* Off-Peak Card */}
                    <Box
                        bg="#faf5ff"

                        borderRadius="15px"
                        p={5}
                        flex="1"
                        minW="200px"
                    >
                        <HStack spacing={2} mb={2}>
                            <FiTrendingDown size={18} color="#6e11b0" />
                            <Text fontSize="sm" fontWeight="medium" color="#6e11b0">
                                Off-Peak
                            </Text>
                        </HStack>

                        <Text fontSize="2xl" fontWeight="bold" color="#8200db">
                            {analyticsData?.offPeakHour?.hour || 0}

                        </Text>

                        <Text fontSize="xs" color="#9810fa" mt={1}>
                            {analyticsData?.offPeakHour?.count} messages
                        </Text>
                    </Box>
                </Flex>


                <Box mt={4}>
                    {analyticsData?.peakHourChannelBreakdown?.hour ? <Text fontSize="md" fontWeight="semibold" color="gray.700">Peak Hour Channel Breakdown ({analyticsData?.peakHourChannelBreakdown?.hour})</Text> : ""}



                    <Flex gap={4} flexWrap="wrap">
                        {analyticsData?.peakHourChannelBreakdown?.channels?.map((item) => {
                            const config = channelConfig[item.channel] || {

                                color: "#6b7280"
                            };

                            return (
                                <Box
                                    key={item.channel}
                                    bg="white"
                                    p={2}
                                    rounded="lg"
                                    flex="1"
                                    minW="150px"
                                    textAlign="center"

                                >


                                    <Text fontSize="xs" fontWeight="semibold" color={config.color} >
                                        {getChannelLabel(item.channel)}
                                    </Text>

                                    <Text fontSize="xl" fontWeight="bold">
                                        {item.count}
                                    </Text>

                                    <Text fontSize="xs" color="#64748b">
                                        {item.percent}%
                                    </Text>
                                </Box>
                            );
                        })}
                    </Flex>
                </Box>

            </Box>
        </>
    )
}

export default UsageAnalytics